
import React, {useEffect} from 'react';
import {
  IconButton,
  Tooltip
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppDataGrid from '@/components/common/AppDataGrid';
import { store } from '@/service/store';
import {useSignals} from "@preact/signals-react/runtime";

const CustomerDataGrid: React.FC = () => {
  useSignals();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleOpenCustomerDetails = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  useEffect(() => {
    store.customers.load();
  }, [])

  const columns: GridColDef[] = [
    {
      field: 'customerNumber',
      headerName: t('customers.customerNumber'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'businessName',
      headerName: t('customers.businessName'),
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'address',
      headerName: t('customers.address'),
      flex: 2,
      minWidth: 200,
      valueGetter: (value: any, row: any) => {
        const parts = [
          row.streetOne,
          row.streetTwo,
          row.zipCode && row.city ? `${row.zipCode} ${row.city}` : row.zipCode || row.city
        ].filter(Boolean);
        return parts.join(', ');
      },
    },
    {
      field: 'phoneNumber',
      headerName: t('customers.phone'),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'taxNumber',
      headerName: t('customers.taxNumber'),
      flex: 1.5,
      minWidth: 200,
      valueGetter: (value: any, row: any) => {
        const taxNumber = row.taxNumber;
        const taxNumberLocal = row.taxNumberLocal;

        if (taxNumber && taxNumberLocal) {
          return `${taxNumber} (${taxNumberLocal})`;
        } else if (taxNumber) {
          return taxNumber;
        } else if (taxNumberLocal) {
          return taxNumberLocal;
        }
        return '';
      },
    },
    {
      field: 'vatId',
      headerName: t('customers.vatId'),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'taxId',
      headerName: t('customers.taxId'),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'actions',
      headerName: t('events.actions'),
      sortable: false,
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={t('customers.openDetails')}>
          <IconButton
            onClick={() => handleOpenCustomerDetails(params.row.customerNumber)}
            size="small"
            sx={{
              color: 'primary.main',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <AppDataGrid
      rows={store.customers.getCustomerList().value ?? []}
      columns={columns}
      getRowId={(row) => row.customerNumber}
      loading={store.customers.isLoading.value}
      density="compact"
      initialState={{
        sorting: {
          sortModel: [{ field: 'customerNumber', sort: 'asc' }],
        },
        pagination: {
          paginationModel: {
            pageSize: 25,
          },
        },
        columns: {
          columnVisibilityModel: {
            taxNumber: false,
            vatId: false,
            taxId: false,
          },
        },
      }}
    />
  );
};

export default CustomerDataGrid;
